import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 10000;
const SEEN_STORAGE_KEY = "arbe_seen_order_ids";

// Short pleasant "ding" using Web Audio API (no file needed)
let _audioCtx = null;
function playDing() {
  try {
    if (!_audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      _audioCtx = new Ctx();
    }
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    // Two-tone chime
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + i * 0.14;
      gain.gain.setValueAtTime(0.001, start);
      gain.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
  } catch (e) {
    // silent fail — sound is a nice-to-have
  }
}

/**
 * Polls /orders/live every 10 s and fires a toast + ding for every new order
 * that wasn't previously seen in this browser (tracked in localStorage so a
 * page refresh doesn't re-alert).
 *
 * Also exposes `onAlert` callback so a display screen can react visually.
 */
export function useOrderAlerts({ enabled = true, onAlert, onData } = {}) {
  const seenRef = useRef(null);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    // Load previously-seen ids
    try {
      const raw = localStorage.getItem(SEEN_STORAGE_KEY);
      seenRef.current = new Set(raw ? JSON.parse(raw) : []);
    } catch (_) {
      seenRef.current = new Set();
    }

    let timer;
    let cancelled = false;

    const persist = () => {
      try {
        const arr = Array.from(seenRef.current).slice(-200); // cap
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(arr));
      } catch (_) { /* noop */ }
    };

    const tick = async () => {
      try {
        const { data } = await api.get("/orders/live");
        if (cancelled) return;
        if (onData) onData(data);

        const newOrders = [];
        for (const o of data) {
          if (!seenRef.current.has(o.id)) {
            if (!firstLoadRef.current) newOrders.push(o);
            seenRef.current.add(o.id);
          }
        }
        if (newOrders.length > 0) {
          playDing();
          newOrders.forEach((o) => {
            toast.success(`New order #${o.number} — ₱${(o.total || 0).toFixed(2)}`, {
              description: `${o.items?.length || 0} item${o.items?.length === 1 ? "" : "s"} · ${o.address || "Walk-in"}`,
              duration: 8000,
            });
            if (onAlert) onAlert(o);
          });
        }
        firstLoadRef.current = false;
        persist();
      } catch (_) {
        // swallow — keep polling
      }
    };

    tick();
    timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled, onAlert, onData]);
}
